import React, {FC, Fragment, useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp, faCirclePlus,
  faPlus, faSave,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import {QuantityType} from "../../../lib/src/api/dto/QuantityType"
import {QuantityTypeTemplate} from "../../../lib/src/api/dto/QuantityTypeTemplate"
import {QuantityTypeTemplateQuestion} from "../../../lib/src/api/dto/QuantityTypeTemplateQuestion"
import {Button, IconButton} from "@traas/lib/src/components/form/Button";
import { Input } from "@traas/lib/src/components/form/Input";
import { Toggle } from "@traas/lib/src/components/form/Toggle";
import {TaskTypeTemplatesQuestionEditor} from "./TaskTypeTemplatesQuestionEditor";
import {useApiCall} from "../api/api";
import {v4} from "uuid";

export const TaskTypeTemplatesEditor: FC<{taskType: QuantityType}> = props => {
  const {addQuantityTypeTemplate, editQuantityTypeTemplates} = useApiCall()
  const orderedTasks = useMemo(() => {
    return props.taskType.templates.sort((a,b) => a.order > b.order ? 1 : -1)
  }, [props.taskType.templates])

  return <div>
    <div className={'flex items-end justify-between mb-6'}>
      <h2 className={'font-medium text-xl'}>Taken</h2>
      <Button type={'primary'} size={'md'} onClick={async () => {
        await addQuantityTypeTemplate(props.taskType.id, {
          description: props.taskType.name,
          order: props.taskType.templates.length + 1,
          shouldGenerateWorkorder: true,
        })
      }} text={'Toevoegen'} icon={faPlus} />
    </div>
    {orderedTasks.map((t, i) => <Fragment key={i}>
      <Task template={t} taskType={props.taskType} moveUp={async () => {
        const prev: QuantityTypeTemplate|undefined = props.taskType.templates[i - 1]
        const curr: QuantityTypeTemplate|undefined = props.taskType.templates[i]
        if (! prev || ! curr) {
          console.warn('Cannot move task up, no current and previous tasks available.')
          return;
        }
        await editQuantityTypeTemplates(props.taskType.id, [
          {...prev, order: curr.order},
          {...curr, order: prev.order},
        ])
      }} moveDown={async () => {
        const curr: QuantityTypeTemplate|undefined = props.taskType.templates[i]
        const next: QuantityTypeTemplate|undefined = props.taskType.templates[i + 1]
        if (! curr || ! next ) {
          console.warn('Cannot move task down, no current and next tasks available.')
          return;
        }
        await editQuantityTypeTemplates(props.taskType.id, [
          {...curr, order: next.order},
          {...next, order: curr.order},
        ])
      }} />
    </Fragment>)}
  </div>
}

const Task: FC<{taskType: QuantityType, template: QuantityTypeTemplate, moveUp: () => Promise<void>, moveDown: () => Promise<void>}> = props => {
  const {editQuantityTypeTemplate, deleteQuantityTypeTemplate} = useApiCall()
  const [id, setId] = useState<string>(props.template.id)
  const [name, setName] = useState(props.template.description)
  const [shouldGenerateWorkorder, setShouldGenerateWorkorder] = useState<boolean>(props.template.shouldGenerateWorkorder)
  const [questions, setQuestions] = useState(props.template.questions.sort((a,b) => a.order > b.order ? 1 : -1))

  const hasUnsavedChanges = name !== props.template.description ||
                                     shouldGenerateWorkorder !== props.template.shouldGenerateWorkorder ||
                                     JSON.stringify(questions) !== JSON.stringify(props.template.questions)

  useEffect(() => {
    if (id !== props.template.id) {
      setId(props.template.id)
      setName(props.template.description)
      setShouldGenerateWorkorder(props.template.shouldGenerateWorkorder)
      setQuestions(props.template.questions.sort((a,b) => a.order > b.order ? 1 : -1))
    }
  }, [id, props.template.id]);

  const save = async () => {
    await editQuantityTypeTemplate(props.taskType.id, {
      ...props.template,
      description: name,
      shouldGenerateWorkorder: shouldGenerateWorkorder,
      questions: questions,
    })
  }

  useEffect(() => {
    const currentOrder = questions.map(q => ({q:q.id, n:q.order}))
    const newOrder = questions.sort((a,b) => a.order > b.order ? 1 : -1).map((q,i) => ({q:q.id, n:i+1}))
    if (JSON.stringify(currentOrder) !== JSON.stringify(newOrder)) {
      setQuestions(questions.sort((a,b) => a.order > b.order ? 1 : -1).map((q, i) => ({
        ...q,
        order: i + 1,
      })))
    }
  }, [questions]);

  return <div className={`${hasUnsavedChanges ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-slate-200'} shadow-xs rounded-lg border flex items-stretch mb-4`}>
    <div className={'flex flex-col items-center border-r border-slate-200 w-10 py-2'}>
      <IconButton type={'secondary'} size={'sm'} icon={faChevronUp} disabled={props.template.order === 1} onClick={() => props.moveUp()} />
      <IconButton type={'secondary'} size={'sm'} icon={faChevronDown} disabled={props.template.order === props.taskType.templates.length} onClick={() => props.moveDown()} />
    </div>
    <div className={'px-3 py-2 flex-1'}>
      <div className={'grid grid-cols-6 gap-2'}>
        <div className={'col-span-2 -mt-1'}>
          <Input type={'text'} label={''} value={name} onChange={setName} />
        </div>
        <div className={'col-span-2'}>
          <Toggle label={'Werkbon aanmaken bij taak'} size={'sm'} value={shouldGenerateWorkorder} onChange={setShouldGenerateWorkorder} />
        </div>
        <Button disabled={!hasUnsavedChanges} size={'md'} type={'primary'} text={'Opslaan'} icon={faSave} onClick={() => save()} />
        <Button size={'md'} type={'danger'} text={'Verwijderen'} icon={faTimes} onClick={async () => {
          if (confirm('Wil je deze taak verwijderen?')) {
            await deleteQuantityTypeTemplate(props.taskType.id, props.template.id)
          }
        }} />
      </div>
      <h3 className={'mt-4 mb-3 text-lg font-medium'}>Vragenlijst</h3>
      {questions.map((q, i) => {
        return <Fragment key={i}>
          {i === 0 ? <></> : <Between onClick={() => { setQuestions([
            ...questions,
            new QuantityTypeTemplateQuestion(v4(), i+0.5, "boolean", '', true, null, [], null, null, null, null, null, [], null),
          ]) }} />}
          <TaskTypeTemplatesQuestionEditor n={i+1} question={q} updateQuestion={(partial) => {
            setQuestions(old => [...old.map((oldQuestion) => {
              if (oldQuestion.id !== q.id) {
                return oldQuestion
              }
              return {
                ...oldQuestion,
                ...partial,
              }
            })])
          }} deleteQuestion={() => {
            setQuestions(old => [...old.filter(oq => oq.id !== q.id)])
          }} />
        </Fragment>
      })}
      <End onClick={() => { setQuestions([
        ...questions,
        new QuantityTypeTemplateQuestion(v4(), questions.length + 0.5, "boolean", '', true, null, [], null, null, null, null, null, [], null),
      ]) }} />
      {hasUnsavedChanges && <div className={'-mx-3 -mb-2 mt-2 bg-yellow-200 text-yellow-700 flex items-center justify-center h-10'}>
        <div className={'mr-4 font-medium'}>Let op, er niet-opgeslagen wijzigingen!</div>
        <Button type={'secondary'} size={'sm'} icon={faSave} text={'Opslaan'} onClick={() => save()}/>
      </div>}
    </div>
  </div>
}
const Between: FC<{onClick: () => void}> = props => {
  return <div className={'ml-5 border-l border-slate-200 h-10 flex items-center'}>
    <div className={'flex items-center -ml-2 text-transparent hover:text-blue-600 group cursor-pointer'} onClick={() => props.onClick()}>
      <FontAwesomeIcon icon={faCirclePlus} className={'h-4 w-4 text-slate-400 bg-slate-50 group-hover:text-blue-600'} />
      <span className={'text-sm ml-2'}>Nieuwe vraag</span>
    </div>
  </div>
}
const End: FC<{onClick: () => void}> = props => {
  return <div className={'ml-5 border-l border-slate-200 h-5 mb-3 flex items-end'}>
    <div className={'flex items-center -ml-2 -mb-2 text-transparent hover:text-blue-600 group cursor-pointer'} onClick={() => props.onClick()}>
      <FontAwesomeIcon icon={faCirclePlus} className={'h-4 w-4 text-slate-400 bg-slate-50 group-hover:text-blue-600'} />
      <span className={'text-sm ml-2'}>Nieuwe vraag</span>
    </div>
  </div>
}
